// =============================================================================
// WeatherWidget — purpose-built for the Battery Care dashboard.
//
// Not a wrapper around <Weather/> anymore — the lg <Weather/> doesn't fit a
// dashboard cell and the md version doesn't show the forecast strip. This
// component owns its own fetch + layout, sized to live inside a .w-shell of
// any reasonable size, with battery-porter intelligence baked into the copy:
// tonight's low is highlighted when cold-soak risk kicks in (≤ 40°F).
// =============================================================================
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../auth/AuthProvider.jsx";

const FALLBACK = { lat: 39.16, lng: -84.45, label: "Cincinnati, OH" };

export function WeatherWidget({ config }) {
  const { profile } = useAuth();
  // Resolution chain: explicit widget config → store profile → fallback.
  const lat = config?.lat ?? profile?.storeLat ?? null;
  const lng = config?.lng ?? profile?.storeLng ?? null;
  const zip = config?.zip ?? profile?.storeZip ?? null;
  const unit = config?.unit || "fahrenheit";

  const { data, coords, error } = useWeather({ lat, lng, zip, unit });

  return (
    <div className="w-shell weather-widget">
      <header className="w-head">
        <div className="w-eye">
          <span className="w-eye-dot" />
          OUTSIDE · {coords?.label?.toUpperCase() || "LOADING"}
        </div>
        <h3 className="w-h3">Conditions at the <em>lot</em>.</h3>
      </header>

      {!data && !error && <div className="w-loading">Reading the sky…</div>}
      {error && !data && (
        <div className="w-empty">
          <div className="w-empty-h">Weather unavailable.</div>
          <div className="w-empty-s">Open-Meteo didn't respond. We'll retry in a few minutes.</div>
        </div>
      )}

      {data && <WeatherBody data={data} unit={unit} />}
    </div>
  );
}

function WeatherBody({ data, unit }) {
  const tempUnit = unit === "fahrenheit" ? "°F" : "°C";
  const tempNow  = Math.round(data.current?.temperature_2m ?? 0);
  const codeNow  = data.current?.weather_code ?? 0;
  const wind     = Math.round(data.current?.wind_speed_10m ?? 0);
  const windUnit = unit === "fahrenheit" ? "mph" : "km/h";
  const decoded  = decodeWmoCode(codeNow);

  const highToday = Math.round(data.daily?.temperature_2m_max?.[0] ?? 0);
  const lowToday  = Math.round(data.daily?.temperature_2m_min?.[0] ?? 0);

  // Cold-soak threshold for lead-acid: every 10°F below 80°F drops cranking
  // power roughly 10%. We highlight tonight's low aggressively below 40°F.
  const tonightCold = unit === "fahrenheit" ? lowToday <= 40 : lowToday <= 4;
  const tonightSubzero = unit === "fahrenheit" ? lowToday <= 20 : lowToday <= -7;

  // 3-day forward forecast (skip today).
  const forecast = useMemo(() => {
    const days = Array.isArray(data.daily?.time) ? data.daily.time : [];
    const out = [];
    for (let i = 1; i < days.length && out.length < 3; i++) {
      const code = data.daily.weather_code?.[i];
      const hi   = data.daily.temperature_2m_max?.[i];
      const lo   = data.daily.temperature_2m_min?.[i];
      const pop  = data.daily.precipitation_probability_max?.[i];
      if (code == null) continue;
      const dt = new Date(`${days[i]}T12:00:00`);
      const label = i === 1 ? "Tomorrow" : dt.toLocaleDateString(undefined, { weekday: "short" });
      out.push({
        key: days[i], label,
        decoded: decodeWmoCode(code),
        hi: Math.round(hi ?? 0),
        lo: Math.round(lo ?? 0),
        pop: typeof pop === "number" ? pop : null,
      });
    }
    return out;
  }, [data]);

  return (
    <div className="ww-body">
      {/* Current conditions — icon + big temp + condition label */}
      <div className="ww-now">
        <WeatherIcon name={decoded.icon} size={44} />
        <div className="ww-now-text">
          <div className="ww-temp">
            {tempNow}<span className="ww-temp-unit">{tempUnit}</span>
          </div>
          <div className="ww-cond">{decoded.label}</div>
        </div>
        <div className="ww-meta">
          <div className="ww-meta-row">
            <span className="ww-meta-k">H</span>
            <span className="ww-meta-v">{highToday}°</span>
          </div>
          <div className="ww-meta-row">
            <span className="ww-meta-k">L</span>
            <span className={`ww-meta-v${tonightSubzero ? " freeze" : tonightCold ? " cold" : ""}`}>
              {lowToday}°
            </span>
          </div>
          <div className="ww-meta-row dim">
            <span className="ww-meta-k">Wind</span>
            <span className="ww-meta-v">{wind} {windUnit}</span>
          </div>
        </div>
      </div>

      {/* Battery-relevant note — only shows when cold matters tonight. */}
      {tonightCold && (
        <div className={`ww-note${tonightSubzero ? " freeze" : ""}`}>
          {tonightSubzero
            ? <>Cold soak tonight at <em>{lowToday}°</em>. Weak units will struggle in the morning.</>
            : <>Tonight bottoms at <em>{lowToday}°</em>. Watch the borderline units before close.</>}
        </div>
      )}

      {/* 3-day forward strip */}
      {forecast.length > 0 && (
        <div className="ww-forecast">
          {forecast.map((d) => (
            <div key={d.key} className="ww-day">
              <div className="ww-day-label">{d.label}</div>
              <WeatherIcon name={d.decoded.icon} size={22} />
              <div className="ww-day-temps">
                <span className="ww-day-hi">{d.hi}°</span>
                <span className="ww-day-lo">{d.lo}°</span>
              </div>
              {d.pop != null && d.pop >= 30 && (
                <div className="ww-day-pop">{d.pop}%</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// useWeather — fetches Open-Meteo current+daily for given coords or ZIP.
// Refreshes every 10 minutes. ZIP→coords cached in localStorage.
// -----------------------------------------------------------------------------
function useWeather({ lat, lng, zip, unit }) {
  const [coords, setCoords] = useState(() => (
    typeof lat === "number" && typeof lng === "number"
      ? { lat, lng, label: "" }
      : null
  ));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number") {
      setCoords((c) => (c?.lat === lat && c?.lng === lng ? c : { lat, lng, label: "" }));
      return;
    }
    if (!zip) {
      setCoords(FALLBACK);
      return;
    }
    const cacheKey = `sevric.weather.zip.${zip}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.lat && parsed?.lng) { setCoords(parsed); return; }
      }
    } catch { /* ignore */ }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`);
        if (!r.ok) throw new Error(`zip ${r.status}`);
        const j = await r.json();
        const place = j?.places?.[0];
        if (!place) throw new Error("no place");
        const c = {
          lat: parseFloat(place.latitude),
          lng: parseFloat(place.longitude),
          label: `${place["place name"]}, ${place["state abbreviation"]}`,
        };
        if (cancelled) return;
        try { localStorage.setItem(cacheKey, JSON.stringify(c)); } catch {}
        setCoords(c);
      } catch {
        if (!cancelled) setCoords(FALLBACK);
      }
    })();
    return () => { cancelled = true; };
  }, [lat, lng, zip]);

  useEffect(() => {
    if (!coords) return;
    let cancelled = false;
    async function load() {
      try {
        const url = "https://api.open-meteo.com/v1/forecast"
          + `?latitude=${coords.lat}&longitude=${coords.lng}`
          + "&current=temperature_2m,weather_code,wind_speed_10m"
          + "&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max"
          + `&temperature_unit=${unit}`
          + (unit === "fahrenheit" ? "&wind_speed_unit=mph" : "")
          + "&timezone=auto&forecast_days=4";
        const r = await fetch(url);
        if (!r.ok) throw new Error(`weather ${r.status}`);
        const j = await r.json();
        if (!cancelled) { setData(j); setError(null); }
      } catch (err) {
        if (!cancelled) setError(err);
      }
    }
    load();
    const id = setInterval(load, 600_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [coords?.lat, coords?.lng, unit]);

  return { data, coords, error };
}

// -----------------------------------------------------------------------------
// WMO code → label + icon
// -----------------------------------------------------------------------------
function decodeWmoCode(code) {
  if (code === 0)                          return { label: "Clear",         icon: "sun" };
  if (code === 1 || code === 2)            return { label: "Partly cloudy", icon: "sun-cloud" };
  if (code === 3)                          return { label: "Overcast",      icon: "cloud" };
  if (code === 45 || code === 48)          return { label: "Foggy",         icon: "fog" };
  if (code >= 51 && code <= 57)            return { label: "Drizzle",       icon: "drizzle" };
  if (code >= 61 && code <= 67)            return { label: "Rain",          icon: "rain" };
  if (code >= 71 && code <= 77)            return { label: "Snow",          icon: "snow" };
  if (code >= 80 && code <= 82)            return { label: "Showers",       icon: "rain" };
  if (code === 85 || code === 86)          return { label: "Snow showers",  icon: "snow" };
  if (code >= 95)                          return { label: "Thunderstorm",  icon: "storm" };
  return { label: "—", icon: "cloud" };
}

function WeatherIcon({ name, size = 28 }) {
  const props = {
    width: size, height: size, viewBox: "0 0 24 24",
    fill: "none", stroke: "currentColor", strokeWidth: "1.6",
    strokeLinecap: "round", strokeLinejoin: "round",
    "aria-hidden": "true",
  };
  switch (name) {
    case "sun": return (
      <svg {...props}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
    case "sun-cloud": return (
      <svg {...props}>
        <circle cx="8" cy="9" r="3" />
        <path d="M8 3v1M3 9h1M5.05 5.93l.7.7" />
        <path d="M19 17a3 3 0 0 0-2.83-3.99 5 5 0 0 0-9.81 1.45A3 3 0 0 0 8 21h11a3 3 0 0 0 0-6z" transform="translate(0 -2)" />
      </svg>
    );
    case "cloud": return (
      <svg {...props}>
        <path d="M17.5 19a4.5 4.5 0 0 0 .92-8.91 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 19z" />
      </svg>
    );
    case "fog": return (
      <svg {...props}>
        <path d="M3 8h18M3 12h14M3 16h18M3 20h12" />
      </svg>
    );
    case "drizzle": return (
      <svg {...props}>
        <path d="M17 14a4 4 0 0 0 .88-7.9 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 14" />
        <path d="M8 18l-1 2M12 18l-1 2M16 18l-1 2" />
      </svg>
    );
    case "rain": return (
      <svg {...props}>
        <path d="M17 14a4 4 0 0 0 .88-7.9 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 14" />
        <path d="M9 19l-1.5 3M13 19l-1.5 3M17 19l-1.5 3" />
      </svg>
    );
    case "snow": return (
      <svg {...props}>
        <path d="M17 14a4 4 0 0 0 .88-7.9 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 14" />
        <path d="M8 18v4M12 19v4M16 18v4M7 20h2M11 21h2M15 20h2" />
      </svg>
    );
    case "storm": return (
      <svg {...props}>
        <path d="M17 14a4 4 0 0 0 .88-7.9 6 6 0 0 0-11.74 1.05A4 4 0 0 0 7 14" />
        <path d="M13 14l-3 5h3l-2 4" />
      </svg>
    );
    default: return (
      <svg {...props}>
        <circle cx="12" cy="12" r="6" />
      </svg>
    );
  }
}
